import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { 
  Briefcase, 
  Edit, 
  Save,
  Loader2, 
  CheckCircle,
  Calendar,
  DollarSign,
  Building2,
  Clock,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPLOYMENT_STATUS_AT_ENROLLMENT_OPTIONS = ["Employed", "Self-Employed", "Unemployed"];
const EMPLOYMENT_STATUS_OPTIONS = ["Full-Time", "Part-Time"];

const EmploymentOutcomeCard = ({ caseId, caseData, onUpdate, onRef, onCompletionStatusChange }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [outcomeData, setOutcomeData] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  const [formData, setFormData] = useState({
    employment_status_at_enrollment: '',
    start_date: '',
    employer_name: '',
    hourly_wage: '',
    status: ''
  });

  // Expose functions to parent via ref callback
  useEffect(() => {
    if (onRef) {
      onRef({
        openEditDialog: () => setShowEditDialog(true),
        isComplete: isComplete
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComplete]);

  // Determine if this is a SNAP E&T case
  const isSnapEtCase = caseData?.program_name?.toLowerCase().includes('snap') && 
                       caseData?.program_name?.toLowerCase().includes('e&t');

  useEffect(() => {
    if (isSnapEtCase && caseId) {
      fetchEmploymentOutcome();
    }
  }, [caseId, isSnapEtCase]);

  const fetchEmploymentOutcome = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/cases/${caseId}/employment-outcome`);
      const data = response.data;
      setOutcomeData(data.employment_outcome || {});
      const completionStatus = data.is_complete || false;
      setIsComplete(completionStatus);
      
      // Notify parent of initial completion status
      if (onCompletionStatusChange) {
        onCompletionStatusChange(completionStatus);
      }
      
      // Pre-populate form data
      if (data.employment_outcome) {
        setFormData({
          employment_status_at_enrollment: data.employment_outcome.employment_status_at_enrollment || '',
          start_date: data.employment_outcome.start_date || '',
          employer_name: data.employment_outcome.employer_name || '',
          hourly_wage: data.employment_outcome.hourly_wage !== null && data.employment_outcome.hourly_wage !== undefined 
            ? String(data.employment_outcome.hourly_wage) 
            : '',
          status: data.employment_outcome.status || ''
        });
      }
    } catch (error) {
      console.error('Failed to fetch employment outcome:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEdit = () => {
    // Reset form data from outcomeData
    if (outcomeData) {
      setFormData({
        employment_status_at_enrollment: outcomeData.employment_status_at_enrollment || '',
        start_date: outcomeData.start_date || '',
        employer_name: outcomeData.employer_name || '',
        hourly_wage: outcomeData.hourly_wage !== null && outcomeData.hourly_wage !== undefined 
          ? String(outcomeData.hourly_wage) 
          : '',
        status: outcomeData.status || ''
      });
    }
    setShowEditDialog(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/cases/${caseId}/employment-outcome`, formData);
      toast.success('Employment outcome updated successfully');
      setOutcomeData(response.data.employment_outcome);
      const newIsComplete = response.data.is_complete;
      setIsComplete(newIsComplete);
      setShowEditDialog(false);
      if (onUpdate) onUpdate(newIsComplete);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save employment outcome');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/cases/${caseId}/employment-outcome`);
      toast.success('Employment outcome deleted');
      setOutcomeData({});
      setIsComplete(false);
      setFormData({ employment_status_at_enrollment: '', start_date: '', employer_name: '', hourly_wage: '', status: '' });
      if (onCompletionStatusChange) onCompletionStatusChange(false);
      if (onUpdate) onUpdate(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete employment outcome');
    } finally {
      setDeleting(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return 'Not set';
    const num = parseFloat(value);
    if (isNaN(num)) return 'Not set';
    return `$${num.toFixed(2)}`;
  };

  // Don't render if not a SNAP E&T case
  if (!isSnapEtCase) {
    return null;
  }

  const hasOutcomeData = outcomeData && (
    outcomeData.employment_status_at_enrollment || 
    outcomeData.employer_name || 
    outcomeData.start_date
  );

  return (
    <>
      <Card className="card-shadow border-green-200 bg-gradient-to-br from-green-50 to-white" data-testid="employment-outcome-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2 text-green-800">
              <Briefcase className="w-5 h-5" />
              Participant Employment Outcome
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleOpenEdit}
                className="border-green-300 hover:bg-green-100"
                data-testid="edit-employment-outcome-btn"
              >
                <Edit className="w-4 h-4 mr-1" />
                {hasOutcomeData ? 'Edit' : 'Complete'}
              </Button>
              {hasOutcomeData && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDelete}
                  disabled={deleting}
                  className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                  data-testid="delete-employment-outcome-btn"
                >
                  {deleting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
                  Delete
                </Button>
              )}
            </div>
          </div>
          <CardDescription>Employment status and outcome tracking</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 text-center">
              <Loader2 className="w-6 h-6 animate-spin mx-auto" />
              <p className="text-sm text-muted-foreground mt-2">Loading...</p>
            </div>
          ) : hasOutcomeData ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Briefcase className="w-4 h-4" />
                    Status at Enrollment
                  </div>
                  <p className="font-medium">{outcomeData.employment_status_at_enrollment || 'Not set'}</p>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Building2 className="w-4 h-4" />
                    Employer
                  </div>
                  <p className="font-medium">{outcomeData.employer_name || 'Not set'}</p>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="w-4 h-4" />
                    Employment Status
                  </div>
                  <Badge 
                    variant={outcomeData.status === 'Full-Time' ? 'default' : 'secondary'}
                    className={outcomeData.status === 'Full-Time' ? 'bg-green-600' : ''}
                  >
                    {outcomeData.status || 'Not set'}
                  </Badge>
                </div>
              </div>

              {/* Employment Details */}
              <div className="bg-green-50/50 rounded-lg p-3 border border-green-100">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                      <Calendar className="w-4 h-4" />
                      Start Date
                    </div>
                    <p className="font-medium">{outcomeData.start_date || 'Not set'}</p>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                      <DollarSign className="w-4 h-4" />
                      Hourly Wage
                    </div>
                    <p className="font-medium">{formatCurrency(outcomeData.hourly_wage)}</p>
                  </div>
                </div>
              </div>

              {/* Employment Status - Read-only, shown when card is complete */}
              {isComplete && (
                <div className="flex items-center gap-2 p-3 bg-green-100 rounded-lg border border-green-200">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-medium text-green-800">Employment Status: Employed</p>
                    <p className="text-xs text-green-600">Card complete - All fields filled</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-6 bg-amber-50 rounded-lg border border-amber-200">
              <Briefcase className="w-10 h-10 mx-auto mb-2 text-amber-600" />
              <p className="text-amber-800 font-medium">Employment Outcome Required</p>
              <p className="text-sm text-amber-700 mb-3">
                Complete the employment outcome details for this participant.
              </p>
              <Button 
                onClick={handleOpenEdit} 
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="complete-employment-outcome-btn"
              >
                Complete Now
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Participant Employment Outcome</DialogTitle>
            <DialogDescription>
              Track the employment status and outcome for this SNAP E&T participant
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Employment Status at Enrollment */}
            <div className="space-y-2">
              <Label>Employment Status at Enrollment *</Label>
              <Select
                value={formData.employment_status_at_enrollment || "none"}
                onValueChange={(value) => setFormData({
                  ...formData, 
                  employment_status_at_enrollment: value === "none" ? "" : value
                })}
              >
                <SelectTrigger data-testid="employment-status-at-enrollment-select">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select status</SelectItem>
                  {EMPLOYMENT_STATUS_AT_ENROLLMENT_OPTIONS.map((status) => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Start Date */}
            <div className="space-y-2">
              <Label>Start Date *</Label>
              <Input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                data-testid="employment-start-date"
              />
            </div>

            {/* Employer Name */}
            <div className="space-y-2">
              <Label>Employer Name *</Label>
              <Input
                value={formData.employer_name}
                onChange={(e) => setFormData({...formData, employer_name: e.target.value})}
                placeholder="e.g., ABC Company"
                data-testid="employer-name-input"
              />
            </div>

            {/* Hourly Wage */}
            <div className="space-y-2">
              <Label>Hourly Wage *</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.hourly_wage}
                  onChange={(e) => setFormData({...formData, hourly_wage: e.target.value})}
                  placeholder="0.00"
                  className="pl-8"
                  data-testid="hourly-wage-input"
                />
              </div>
            </div>

            {/* Status (Full-Time / Part-Time) */}
            <div className="space-y-2">
              <Label>Status *</Label>
              <Select
                value={formData.status || "none"}
                onValueChange={(value) => setFormData({
                  ...formData, 
                  status: value === "none" ? "" : value
                })}
              >
                <SelectTrigger data-testid="employment-status-select">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select status</SelectItem>
                  {EMPLOYMENT_STATUS_OPTIONS.map((status) => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Info about completion */}
            <div className="text-sm text-muted-foreground bg-gray-50 p-3 rounded-md">
              <p className="font-medium text-gray-700 mb-1">Employment Status Display</p>
              <p>Once all fields are completed and saved, the Employment Status will automatically display as "Employed".</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              data-testid="save-employment-outcome-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Outcome
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EmploymentOutcomeCard;
